// Docs navigation landing page at "/docs-home". Leads with how to add a project
// to Shuttle (the guide in docs/add-your-project/), then queries the catalog
// REST API for all entities with the backstage.io/techdocs-ref annotation and
// renders a card for each — new projects appear automatically when their
// catalog entity is registered with that annotation.

import { useState, useEffect } from 'react';
import {
  createFrontendModule,
  PageBlueprint,
} from '@backstage/frontend-plugin-api';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { makeStyles } from '@material-ui/core';
import { Link, Progress } from '@backstage/core-components';

type TechDocsEntity = {
  kind: string;
  metadata: {
    name: string;
    namespace?: string;
    title?: string;
    description?: string;
    annotations?: Record<string, string>;
  };
};

const useStyles = makeStyles({
  page: {
    padding: '48px 56px',
    maxWidth: 900,
  },
  heading: {
    margin: 0,
    fontSize: '2rem',
    fontWeight: 700,
    color: '#FF286E',
    letterSpacing: '0.01em',
  },
  subtitle: {
    marginTop: 8,
    marginBottom: 24,
    fontSize: '1rem',
    color: 'rgba(255,255,255,0.6)',
  },
  intro: {
    marginBottom: 40,
    padding: '24px 28px',
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderRadius: 8,
    borderLeft: '3px solid #FF286E',
    fontSize: '0.875rem',
    color: 'rgba(255,255,255,0.6)',
    lineHeight: 1.7,
  },
  introHeading: {
    margin: '0 0 8px',
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.1em',
    textTransform: 'uppercase' as const,
    color: 'rgba(255,255,255,0.35)',
  },
  introTitle: {
    margin: '0 0 8px',
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#FFFFFF',
  },
  introList: {
    margin: '12px 0 0',
    paddingLeft: 20,
    '& li': {
      marginBottom: 6,
    },
  },
  introStep: {
    color: 'rgba(255,255,255,0.85)',
    fontWeight: 600,
  },
  introCta: {
    display: 'inline-block',
    marginTop: 16,
    padding: '8px 16px',
    borderRadius: 4,
    backgroundColor: '#FF286E',
    color: '#FFFFFF',
    fontWeight: 600,
    '&:hover': {
      backgroundColor: '#FF5A8E',
      color: '#FFFFFF',
      textDecoration: 'none',
    },
  },
  sectionHeading: {
    margin: '0 0 16px',
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.1em',
    textTransform: 'uppercase' as const,
    color: 'rgba(255,255,255,0.35)',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 24,
  },
  card: {
    backgroundColor: '#0A1128',
    borderRadius: 8,
    padding: '28px 24px',
    border: '1px solid rgba(255,255,255,0.08)',
    textDecoration: 'none',
    display: 'block',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    '&:hover': {
      borderColor: 'rgba(255,40,110,0.5)',
      boxShadow: '0 0 0 1px rgba(255,40,110,0.2)',
      textDecoration: 'none',
    },
  },
  cardIcon: {
    fontSize: '1.8rem',
    marginBottom: 12,
    display: 'block',
  },
  cardTitle: {
    margin: 0,
    fontSize: '1.1rem',
    fontWeight: 700,
    color: '#FFFFFF',
    marginBottom: 8,
  },
  cardDesc: {
    margin: 0,
    fontSize: '0.875rem',
    color: 'rgba(255,255,255,0.55)',
    lineHeight: 1.5,
  },
  empty: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: '0.875rem',
  },
});

// The "Add your project" guide, docs/add-your-project/ in this repository.
const GUIDE_PATH =
  '/docs/default/component/silk-shuttle-portal/add-your-project';

const GUIDE_INTRO =
  "Shuttle is the home for the team's internal projects. Any silk-us repository can have a catalog page and a documentation site here, listed below with the others. It takes three small files in your repository and one line in Shuttle's catalog. After that, merging to main publishes your docs: nothing to build or deploy.";

const GUIDE_STEPS = [
  {
    page: 'write-your-docs',
    title: 'Write your docs',
    detail: 'mkdocs.yml and a docs/ folder of Markdown.',
  },
  {
    page: 'describe-your-project',
    title: 'Describe your project',
    detail:
      'catalog/component.yaml: what it is, who owns it, where its docs are.',
  },
  {
    page: 'register',
    title: 'Register it',
    detail:
      "One line in Shuttle's catalog/all.yaml. Your card appears within a minute of the merge.",
  },
];

const INTERNAL_ONLY =
  "Shuttle is for Silk people only. Documentation written for customers belongs on Silk's customer-facing docs site.";

function docsPath(entity: TechDocsEntity): string {
  const ns = entity.metadata.namespace ?? 'default';
  return `/docs/${ns}/${entity.kind.toLowerCase()}/${entity.metadata.name}`;
}

const DocsHomePage = () => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [entities, setEntities] = useState<TechDocsEntity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    discoveryApi
      .getBaseUrl('catalog')
      .then(async baseUrl => {
        const resp = await fetchApi.fetch(`${baseUrl}/entities`);
        const all: TechDocsEntity[] = await resp.json();
        const withDocs = all
          .filter(
            e =>
              e.kind !== 'Template' &&
              e.metadata.annotations?.['backstage.io/techdocs-ref'],
          )
          .sort((a, b) =>
            (a.metadata.title ?? a.metadata.name).localeCompare(
              b.metadata.title ?? b.metadata.name,
            ),
          );
        setEntities(withDocs);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [discoveryApi, fetchApi]);

  const renderEntities = () => {
    if (loading) return <Progress />;
    if (entities.length === 0) {
      return (
        <p className={classes.empty}>
          No documentation sites found. Register a catalog entity with a{' '}
          <code>backstage.io/techdocs-ref</code> annotation to add one.
        </p>
      );
    }
    return (
      <div className={classes.grid}>
        {entities.map(entity => (
          <Link
            key={`${entity.kind}:${entity.metadata.namespace ?? 'default'}/${
              entity.metadata.name
            }`}
            to={docsPath(entity)}
            className={classes.card}
            underline="none"
          >
            <span className={classes.cardIcon}>📄</span>
            <h2 className={classes.cardTitle}>
              {entity.metadata.title ?? entity.metadata.name}
            </h2>
            {entity.metadata.description && (
              <p className={classes.cardDesc}>{entity.metadata.description}</p>
            )}
          </Link>
        ))}
      </div>
    );
  };

  return (
    <div className={classes.page}>
      <h1 className={classes.heading}>Documentation</h1>
      <p className={classes.subtitle}>
        Guides, reference docs, and runbooks for Silk GTM Technical's internal
        projects.
      </p>
      <div className={classes.intro}>
        <p className={classes.introHeading}>Add your project</p>
        <h2 className={classes.introTitle}>
          Put your project's docs in Shuttle
        </h2>
        <p style={{ margin: 0 }}>{GUIDE_INTRO}</p>
        <ol className={classes.introList}>
          {GUIDE_STEPS.map(step => (
            <li key={step.page}>
              <Link to={`${GUIDE_PATH}/${step.page}/`}>
                <span className={classes.introStep}>{step.title}</span>
              </Link>
              {` — ${step.detail}`}
            </li>
          ))}
        </ol>
        <Link
          to={`${GUIDE_PATH}/`}
          className={classes.introCta}
          underline="none"
        >
          Read the guide →
        </Link>
        <p
          style={{
            margin: '16px 0 0',
            fontSize: '0.8rem',
            color: 'rgba(255,255,255,0.35)',
          }}
        >
          {INTERNAL_ONLY}
        </p>
        {/* Stated once, deliberately. Shuttle is the name used everywhere else
            in the portal and in user documentation; this is the only place the
            underlying framework is named, so that annotation names and upstream
            documentation links still make sense to anyone who goes looking. */}
        <p
          style={{
            margin: '4px 0 0',
            fontSize: '0.8rem',
            color: 'rgba(255,255,255,0.35)',
          }}
        >
          Shuttle is the Silk demo environment's implementation of{' '}
          <a
            href="https://backstage.io"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'rgba(255,255,255,0.45)' }}
          >
            Backstage
          </a>
          .
        </p>
      </div>
      <p className={classes.sectionHeading}>Projects</p>
      {renderEntities()}
    </div>
  );
};

const docsHomePage = PageBlueprint.make({
  name: 'docs-home',
  params: {
    path: '/docs-home',
    noHeader: true,
    loader: async () => <DocsHomePage />,
  },
});

export const docsHomeModule = createFrontendModule({
  pluginId: 'app',
  extensions: [docsHomePage],
});
